import { useState, FormEvent } from 'react';

export default function AdvertiserForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const notes = formData.get('notes') as string;
    if (notes && notes.length > 500) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d66jmbre1k2bdn1q486g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="advertiser-form" className="py-16 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            Request sponsorship information
          </h2>
          <p className="text-base text-gray-600">
            Tell us about your business. We&apos;ll confirm availability and pricing within 48 hours.
          </p>
        </div>

        <form
          id="advertiser-lead-form"
          data-readdy-form
          onSubmit={handleSubmit}
          className="bg-white rounded-lg p-6 shadow-lg border border-gray-200"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
            <div>
              <label htmlFor="company-name" className="block text-sm font-semibold text-gray-900 mb-1.5">
                Company name *
              </label>
              <input
                id="company-name"
                name="company_name"
                required
                className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
              />
            </div>
            <div>
              <label htmlFor="contact-name" className="block text-sm font-semibold text-gray-900 mb-1.5">
                Contact name
              </label>
              <input
                id="contact-name"
                name="contact_name"
                className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-1.5">
                Email *
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
              />
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-1.5">
                Phone *
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                required
                className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
            <div>
              <label htmlFor="website" className="block text-sm font-semibold text-gray-900 mb-1.5">
                Website
              </label>
              <input
                id="website"
                name="website"
                type="url"
                placeholder="https://"
                className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
              />
            </div>
            <div>
              <label htmlFor="zip-code" className="block text-sm font-semibold text-gray-900 mb-1.5">
                ZIP code
              </label>
              <input
                id="zip-code"
                name="zip_code"
                className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
            <div>
              <label htmlFor="target-radius" className="block text-sm font-semibold text-gray-900 mb-1.5">
                Target radius
              </label>
              <select
                id="target-radius"
                name="target_radius"
                className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all cursor-pointer"
              >
                <option value="">Select radius</option>
                <option value="5 miles">5 miles</option>
                <option value="10 miles">10 miles</option>
                <option value="25 miles">25 miles</option>
                <option value="50 miles">50 miles</option>
                <option value="100 miles">100 miles</option>
                <option value="Nationwide">Nationwide</option>
              </select>
            </div>
            <div>
              <label htmlFor="category" className="block text-sm font-semibold text-gray-900 mb-1.5">
                Category
              </label>
              <select
                id="category"
                name="category"
                className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all cursor-pointer"
              >
                <option value="">Select category</option>
                <option value="Home Care Services">Home Care Services</option>
                <option value="Medical Equipment">Medical Equipment</option>
                <option value="Senior Living">Senior Living</option>
                <option value="Financial Services">Financial Services</option>
                <option value="Legal Services">Legal Services</option>
                <option value="Insurance">Insurance</option>
                <option value="Pharmacy">Pharmacy</option>
                <option value="Wellness & Fitness">Wellness & Fitness</option>
                <option value="Food & Nutrition">Food & Nutrition</option>
                <option value="Transportation">Transportation</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label htmlFor="budget-range" className="block text-sm font-semibold text-gray-900 mb-1.5">
                Budget range
              </label>
              <select
                id="budget-range"
                name="budget_range"
                className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all cursor-pointer"
              >
                <option value="">Select budget</option>
                <option value="Under $500/month">Under $500/month</option>
                <option value="$500–$1,000/month">$500–$1,000/month</option>
                <option value="$1,000–$2,500/month">$1,000–$2,500/month</option>
                <option value="$2,500–$5,000/month">$2,500–$5,000/month</option>
                <option value="$5,000+/month">$5,000+/month</option>
              </select>
            </div>
          </div>

          <div className="mb-5">
            <label htmlFor="notes" className="block text-sm font-semibold text-gray-900 mb-1.5">
              Notes (optional)
            </label>
            <textarea
              id="notes"
              name="notes"
              rows={3}
              maxLength={500}
              placeholder="Tell us about your goals, target audience, or any questions..."
              className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all resize-none"
            />
            <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
          </div>

          {submitStatus === 'success' && (
            <div className="mb-5 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800">Thank you! Your request has been submitted. We&apos;ll be in touch within 48 hours.</p>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">Something went wrong. Please try again.</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-[#6B4EFF] to-[#FF4D7D] text-white px-8 py-3.5 rounded-lg text-base font-semibold hover:from-[#5A3EEE] hover:to-[#EE3C6C] transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Request'}
          </button>
        </form>
      </div>
    </section>
  );
}
